// ClientCareTeam — the client profile's physicians, read from the Provider
// Directory. Editing a doctor here edits the directory record, so the change
// reaches every client, form, fax and packet that uses them; the database
// keeps the client's snapshot columns in step (see migration
// 20260921140000). A client whose doctors were typed before the directory
// existed shows that text with a "Link to directory" action instead.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { ROLE_LEVELS } from '../RoleGuard';
import CareTeamEditor from '../directory/CareTeamEditor';
import { Pill } from '../directory/ui';

export default function ClientCareTeam({ client, onChanged }) {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const canEdit = (ROLE_LEVELS[profile?.role] || 0) >= ROLE_LEVELS.nurse;
  const [team, setTeam] = useState(null);
  const ref = useRef(null);
  const loaded = useRef(false);

  // After the team changes (not the initial load), re-read the client so the
  // snapshot columns the rest of the profile reads are fresh — the DB
  // trigger has just rewritten them.
  useEffect(() => {
    if (team === null) return;
    if (!loaded.current) { loaded.current = true; return; }
    onChanged?.();
  }, [team, onChanged]);

  const typedOnly = team && team.length === 0 && (client.doctor_name || client.ordering_physician_name || client.specialist_name);

  return (
    <div className="card" style={{ marginBottom: 12, overflow: 'visible' }}>
      <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Care Team — physicians</div>
        <button type="button" onClick={() => navigate('/general-info?tab=physicians')} style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', fontSize: 12, fontFamily: 'inherit' }}>Provider Directory ↗</button>
      </div>
      <div style={{ padding: '12px 16px' }}>
        {typedOnly && (
          <div style={{ background: 'var(--warn-bg)', border: '1px solid var(--warn-border)', borderRadius: 8, padding: '10px 12px', marginBottom: 10, fontSize: 12.5 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}><Pill tone="warn">Not linked to the directory</Pill><span style={{ color: 'var(--text2)' }}>These were typed before the directory existed. Add the physician below to link them — the details then come from one record.</span></div>
            {client.doctor_name && <div><b>PCP (typed):</b> {client.doctor_name}{client.doctor_practice ? ` · ${client.doctor_practice}` : ''}{client.doctor_phone ? ` · ${client.doctor_phone}` : ''}{client.doctor_fax ? ` · fax ${client.doctor_fax}` : ''}</div>}
            {(client.ordering_physician_name || client.specialist_name) && <div><b>Ordering (typed):</b> {client.ordering_physician_name || client.specialist_name}{client.ordering_physician_practice ? ` · ${client.ordering_physician_practice}` : ''}{client.ordering_physician_phone ? ` · ${client.ordering_physician_phone}` : ''}{client.ordering_physician_fax ? ` · fax ${client.ordering_physician_fax}` : ''}</div>}
          </div>
        )}
        <CareTeamEditor ref={ref} clientId={client.id} onChange={setTeam} showDetails canEdit={canEdit} />
      </div>
    </div>
  );
}
